/**
 * 制御メッセージのフォーマット。
 *
 * メッセージ本文の最終行が次のスキーマに一致するJSONの場合、
 * 表示時にはその行を取り除き、専用UIに置き換える。
 *
 *   { "type": "attachment", "file_id": string, "file_name": string }
 */

export interface AttachmentControl {
	type: "attachment";
	file_id: string;
	file_name: string;
}

export type MessageControl = AttachmentControl;

export interface ParsedMessage {
	text: string;
	controls: MessageControl[];
}

function isAttachmentControl(value: unknown): value is AttachmentControl {
	if (typeof value !== "object" || value === null) return false;
	const v = value as Record<string, unknown>;
	return (
		v.type === "attachment" && typeof v.file_id === "string" && typeof v.file_name === "string"
	);
}

export function parseMessageControls(content: string): ParsedMessage {
	const lines = content.split("\n");
	let lastIdx = lines.length - 1;
	while (lastIdx >= 0 && lines[lastIdx].trim() === "") lastIdx--;
	if (lastIdx < 0) return { text: content, controls: [] };

	const candidate = lines[lastIdx].trim();
	if (!candidate.startsWith("{") || !candidate.endsWith("}")) {
		return { text: content, controls: [] };
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(candidate);
	} catch {
		return { text: content, controls: [] };
	}

	if (!isAttachmentControl(parsed)) return { text: content, controls: [] };

	const remaining = lines.slice(0, lastIdx).join("\n").trimEnd();
	return {
		text: remaining,
		controls: [parsed],
	};
}

/**
 * Anthropic Messages 形式の content block 配列から、表示用の text を抽出する。
 * text ブロック以外 (tool_use 等) はひとまず無視する。
 */
export function extractContentText(content: readonly { type: string }[]): string {
	const parts: string[] = [];
	for (const block of content) {
		if (block.type === "text") {
			const text = (block as { text?: unknown }).text;
			if (typeof text === "string") parts.push(text);
		}
	}
	return parts.join("");
}
